import { createContext, useContext, type ReactElement, type ReactNode } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../components/ui/tooltip';

/** The focus ring every tooltip-bearing status mark shows (each is a tab stop). */
export const FOCUS_RING = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1';

const ProviderScope = createContext(false);

/** One tooltip provider for a surface that draws many marks (a table of rows); every StatusTooltip inside reuses it. */
export function StatusTooltipScope({ children }: { children: ReactNode }) {
  return (
    <ProviderScope.Provider value>
      <TooltipProvider>{children}</TooltipProvider>
    </ProviderScope.Provider>
  );
}

/**
 * What a row's status mark means, on hover and on keyboard focus. The child must
 * be focusable (tabIndex 0) and spread the props it is given: Radix merges its
 * handlers and, while open, aria-describedby into it. Outside a StatusTooltipScope
 * it carries its own provider, so the mark works on every surface that renders it.
 */
export function StatusTooltip({ content, children }: { content: string; children: ReactElement }) {
  const scoped = useContext(ProviderScope);
  const tooltip = (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="top">
        {content}
      </TooltipContent>
    </Tooltip>
  );
  return scoped ? tooltip : <TooltipProvider>{tooltip}</TooltipProvider>;
}
